import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot } from "vue"

import { useBreakpoints } from "@/code/useBreakpoints";
import { inject, nextTick, onMounted, PropType, ref } from "vue";
import { useCart } from "@/store/useCart";
import { FavoritesApi, UmbracoFormsApi } from "@/services/Api";
import { IQuantityBreak } from "@/models/IQuantityBreak";
import { DataLayerApi } from "@/services/Api";
import { Emitter } from "mitt";
import { IUmbracoForm } from "@/models/umbraco-forms/IUmbracoForm";


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductWrapper',
  props: {
  images: {
    type: Array as PropType<Array<string>>,
    required: true,
  },
  price: {
    type: String,
    required: true,
  },
  oldPrice: {
    type: String,
    required: true,
  },
  quantityBreaks: {
    type: Array as PropType<Array<IQuantityBreak>>,
  },
  isB2b: {
    type: Boolean,
    required: true,
  },
  showDiscountedPrice: {
    type: Boolean,
    required: true,
  },
  requestForPriceFormId: {
    type: String,
    required: true,
  },
  requestForFileFormId: {
    type: String,
    required: true,
  },
  isFavorited: {
    type: Boolean,
  },
  sku: {
    type: String,
    required: true,
  },
},
  setup(__props) {

const props = __props;

const cartStore = useCart();


const { isMd } = useBreakpoints();

const images = ref<string[]>(props.images);
const showAccordion = ref<string>();
const maxAccordionHeight = ref<number>(0);
const emitter = inject<Emitter<any>>("eventbus")!;

const onCartClick = async (partNo: string): Promise<void> => {
  await cartStore.addToCart(partNo, quantity.value, true);
  DataLayerApi.createAddToCartEvent(partNo, quantity.value, "product-page");
  if (typeof window !== "undefined" && window.BiTracking) {
    const row = cartStore.rows.find((r) => r.item.partNo === partNo);
    window.BiTracking.addToBasket(partNo, row?.quantity, "", cartStore.id, "");
  }
};

const isFavorited = ref<boolean>(props.isFavorited);
const onFavoriteClick = async (id: string) => {
  if (window.isLoggedIn) {
    isFavorited.value = !isFavorited.value;
    FavoritesApi.post(id, isFavorited.value).then(() => {
      nextTick(() => {
        if (isFavorited.value) {
          emitter.emit("favoriteAdded");
        } else {
          emitter.emit("favoriteRemoved");
        }
      });
    });
  } else {
    openLoginForm();
  }
};

const getAccordionContentHeight = (sourceElement: HTMLElement | null): number => {
  const accordionContent = sourceElement?.closest(".accordion")?.querySelector(".accordion__content");
  return accordionContent?.scrollHeight ?? 0;
};

const onOpenAccordion = (ev: Event, id: string, scrollTo = true) => {
  const target = ev.target as HTMLElement;

  if (!target) {
    return;
  }

  if (showAccordion.value == id) {
    showAccordion.value = undefined;
  } else {
    showAccordion.value = id;
  }

  if (showAccordion.value != undefined) {
    maxAccordionHeight.value = getAccordionContentHeight(target);
    if (scrollTo) {
      setTimeout(() => {
        const boundBox = target.getBoundingClientRect();
        const headerOffset = 85;
        const offsetPosition = boundBox.top + window.scrollY - headerOffset;
        window.scroll({ top: offsetPosition, behavior: "smooth" });
      }, 250);
    }
  } else {
    maxAccordionHeight.value = 0;
  }
};

const onOpenAccordionById = (elementId: string, id: string) => {
  if (showAccordion.value == id) {
    return;
  } else {
    showAccordion.value = id;
  }

  if (showAccordion.value != undefined) {
    maxAccordionHeight.value = getAccordionContentHeight(document.getElementById(elementId));
  } else {
    maxAccordionHeight.value = 0;
  }
};
const onReadMoreProductInformation = () => {
  onOpenAccordionById("product-information", "productInformation");
  setTimeout(() => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const el = document.getElementById("product-information")!;
    const elRect = el.getBoundingClientRect();
    window.scrollTo(0, window.scrollY + elRect.top - 72);
  }, 300);
};
const showDeliveryAccordion = ref<string>();
const maxDeliveryAccordionHeight = ref<number>(0);
const showDeliveryAccordion2 = ref<string>();
const maxDeliveryAccordionHeight2 = ref<number>(0);
const onOpenDeliveryAccordion = (ev: Event, id: string) => {
  if (showDeliveryAccordion.value == id && showDeliveryAccordion2.value == id) {
    showDeliveryAccordion.value = undefined;
    showDeliveryAccordion2.value = undefined;
  } else {
    showDeliveryAccordion.value = id;
    showDeliveryAccordion2.value = id;
  }

  let height = 0;

  if (showDeliveryAccordion.value != undefined && showDeliveryAccordion2.value != undefined) {
    height = getAccordionContentHeight(ev.target as HTMLElement);
  }

  maxDeliveryAccordionHeight.value = height;
  maxDeliveryAccordionHeight2.value = height;
};

const isImageCarouselExpanded = ref<boolean>(false);
if (props.images) {
  isImageCarouselExpanded.value = props.images.length < 4;
}
const onExpandImageCarousel = () => {
  isImageCarouselExpanded.value = !isImageCarouselExpanded.value;
};

const isVariantPickerExpanded = ref<boolean>(false);
const onVariantPickerExpandClick = () => {
  isVariantPickerExpanded.value = !isVariantPickerExpanded.value;
};

const showDiscountedPrice = ref<boolean>(props.showDiscountedPrice);
const price = ref<string>(props.price);
const oldPrice = ref<string>(props.oldPrice);
const quantity = ref<number>(1);

const onQuantityChange = (ev: Event) => {
  quantity.value = parseInt((ev.target as HTMLInputElement).value, 10);
  if (props.quantityBreaks) {
    const quantityBreaks = props.quantityBreaks.filter((x) => x.qtyBreak <= quantity.value);
    let quantityBreak: IQuantityBreak | null = null;
    if (quantityBreaks && quantityBreaks.length > 0) {
      quantityBreak = quantityBreaks.reduce((prevVal, currVal) => {
        if (prevVal && prevVal.qtyBreak > currVal.qtyBreak) {
          return prevVal;
        } else {
          return currVal;
        }
      });
    }
    if (quantityBreak) {
      const newVal = props.isB2b ? quantityBreak.valueFormatted : quantityBreak.valueIncVatFormatted;
      showDiscountedPrice.value = true;
      price.value = newVal;
    } else {
      price.value = props.price;
      showDiscountedPrice.value = props.oldPrice ? props.oldPrice != props.price : false;
    }
  }
};

const onQuantityBatchOption = (val: number, priceVal: string) => {
  quantity.value = val;
  price.value = priceVal;
  showDiscountedPrice.value = true;
};

const onQuantityBatchOptionChange = (val: string) => {
  price.value = val;
};

const isRequestForPriceOpen = ref<boolean>(false);

const openRequestForPrice = () => {
  isRequestForPriceOpen.value = true;
};

const closeRequestForPrice = () => {
  isRequestForPriceOpen.value = false;
};

const requestForPriceFormDefinition = ref<IUmbracoForm | undefined>();

const isRequestForFileOpen = ref<boolean>(false);
const openRequestForFile = () => {
  isRequestForFileOpen.value = true;
};
const closeRequestForFile = () => {
  isRequestForFileOpen.value = false;
};

const requestForFileFormDefinition = ref();

const isLoginFormOpen = ref<boolean>(false);

const closeLoginForm = () => {
  isLoginFormOpen.value = false;
};

const openLoginForm = () => {
  isLoginFormOpen.value = true;
};

const loadRequestForFileForm = async () => {
  try {
    if (props.requestForFileFormId) {
      requestForFileFormDefinition.value = await UmbracoFormsApi.get(props.requestForFileFormId);
    }
  } catch (e) {
    console.log(e);
  }
};

const loadRequestForPriceForm = async () => {
  try {
    if (props.requestForPriceFormId) {
      requestForPriceFormDefinition.value = await UmbracoFormsApi.get(props.requestForPriceFormId);
    }
  } catch (e) {
    console.log(e);
  }
};

const createTrackingEvent = () => {
  window.setTimeout(async () => {
    await DataLayerApi.createItemViewEvent(props.sku, "1");
  }, 500);
};

const activateVideo = () => {
  const videoEl = document.getElementById("video");
  if (videoEl) {
    videoEl.click();
  }
};

onMounted(async () => {
  activateVideo();
  loadRequestForFileForm();
  loadRequestForPriceForm();
  createTrackingEvent();
});

return (_ctx: any,_cache: any) => {
  return _renderSlot(_ctx.$slots, "default", {
    oldPrice: oldPrice.value,
    showDiscountedPrice: showDiscountedPrice.value,
    isMd: _unref(isMd),
    showAccordion: showAccordion.value,
    maxAccordionHeight: maxAccordionHeight.value,
    onOpenAccordion: onOpenAccordion,
    showDeliveryAccordion: showDeliveryAccordion.value,
    maxDeliveryAccordionHeight: maxDeliveryAccordionHeight.value,
    showDeliveryAccordion2: showDeliveryAccordion2.value,
    maxDeliveryAccordionHeight2: maxDeliveryAccordionHeight2.value,
    onOpenDeliveryAccordion: onOpenDeliveryAccordion,
    images: images.value,
    isImageCarouselExpanded: isImageCarouselExpanded.value,
    onExpandImageCarousel: onExpandImageCarousel,
    isVariantPickerExpanded: isVariantPickerExpanded.value,
    onVariantPickerExpandClick: onVariantPickerExpandClick,
    quantity: quantity.value,
    onQuantityChange: onQuantityChange,
    onQuantityBatchOption: onQuantityBatchOption,
    price: price.value,
    onQuantityBatchOptionChange: onQuantityBatchOptionChange,
    onOpenAccordionById: onOpenAccordionById,
    onReadMoreProductInformation: onReadMoreProductInformation,
    onFavoriteClick: onFavoriteClick,
    isFavorited: isFavorited.value,
    onCartClick: onCartClick,
    isRequestForPriceOpen: isRequestForPriceOpen.value,
    openRequestForPrice: openRequestForPrice,
    closeRequestForPrice: closeRequestForPrice,
    requestForPriceFormDefinition: requestForPriceFormDefinition.value,
    isRequestForFileOpen: isRequestForFileOpen.value,
    openRequestForFile: openRequestForFile,
    closeRequestForFile: closeRequestForFile,
    requestForFileFormDefinition: requestForFileFormDefinition.value,
    isLoginFormOpen: isLoginFormOpen.value,
    closeLoginForm: closeLoginForm,
    openLoginForm: openLoginForm
  })
}
}

})